import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Snackbar from "@mui/material/Snackbar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Third Party
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@apollo/client";

// Internal
import Logo from "../../../assets/logo-primary.png";
import en from "../../../data/en.json";
import { UPSERT_USER } from "../../../data/graphql/mutations";

const rightLink = {
  fontSize: 16,
  color: "primary.main",
  mx: 1.5, // Base margin
};

const rightLinkWide = {
  fontSize: 16,
  color: "primary.main",
  mx: 2.5, // Wider margin for items that need more space
};

const rightLinkExtraWide = {
  fontSize: 16,
  color: "primary.main",
  mx: 3.5, // Wider margin for items that need more space
};

const rightLinkNarrow = {
  fontSize: 16,
  color: "primary.main",
  mx: 1, // Narrower margin for items that are too far apart
};

function AppAppBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [upsertUser, { error: UpsertUserError, data: UpsertUserData }] =
    useMutation(UPSERT_USER);

  const { loginWithRedirect, user } = useAuth0();
  const { logout } = useAuth0();
  const { isAuthenticated, isLoading } = useAuth0();

  const [snackbarMessage, setSnackbarMessage] = React.useState<null | string>(
    null
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [publishingAnchorEl, setPublishingAnchorEl] = React.useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = React.useState(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePublishingClick = (event: any) => {
    setPublishingAnchorEl(event.currentTarget);
  };

  const handlePublishingClose = () => {
    setPublishingAnchorEl(null);
  };

  const handleMobileMenuOpen = (event: any) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  React.useEffect(() => {
    if (isAuthenticated) {
      upsertUser({
        variables: {
          email: user?.email,
          auth0_id: user?.sub,
          role: "member",
          name: user?.name,
          picture_url: user?.picture,
        },
      });
    }
  }, [isAuthenticated, user, upsertUser]);

  React.useEffect(() => {
    if (UpsertUserData !== undefined) {
      setSnackbarMessage(en.common.loginSuccess);
    }
    if (UpsertUserError !== undefined) {
      setSnackbarMessage(en.common.loginFail);
    }
  }, [UpsertUserData, UpsertUserError]);

  if (isLoading) {
    return (
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Box component="img" sx={{ height: 24 }} alt="logo" src={Logo} />
            <Link
              variant="h6"
              underline="none"
              href="/"
              sx={{ pl: 2, fontSize: 24, color: "secondary.light" }}
            >
              {en.common.inkBowl}
            </Link>
          </Box>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <React.Fragment>
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
      />
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              minWidth: { xs: 200, md: 250 },
            }}
          >
            <Box component="img" sx={{ height: 24 }} alt="logo" src={Logo} />
            <Link
              variant="h6"
              underline="none"
              href="/"
              sx={{
                pl: 2,
                fontSize: { xs: 20, md: 24 },
                color: "secondary.light",
              }}
            >
              {en.common.inkBowl}
            </Link>
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 650,
              }}
            >
              <Button
                onClick={() => window.open("/about")}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                }}
              >
                {en.marketing.about}
              </Button>

              <Button
                onClick={() => window.open("/features")}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                }}
              >
                {en.marketing.features}
              </Button>

              <Button
                aria-controls="simple-menu"
                aria-haspopup="true"
                onClick={handleClick}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                  minHeight: "auto",
                  textTransform: "uppercase",
                  alignSelf: "center",
                }}
              >
                {en.marketing.learnMore}
              </Button>
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{ ...rightLinkNarrow }}
              >
                <MenuItem onClick={() => window.open("/guidelines")}>
                  {en.marketing.communityGuideline}
                </MenuItem>
                <MenuItem onClick={() => window.open("/publications")}>
                  {en.common.publications}
                </MenuItem>
              </Menu>

              <Button
                onClick={() => window.open("/dashboard")}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                }}
              >
                {en.common.dashboard}
              </Button>

              <Button
                onClick={() => window.open("/community")}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  ...rightLinkNarrow,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                }}
              >
                {en.common.community}
              </Button>

              <Button
                aria-controls="publishing-menu"
                aria-haspopup="true"
                onClick={handlePublishingClick}
                sx={{
                  ...rightLinkWide,
                  mr: 4.5,
                  color: "secondary.light",
                  whiteSpace: "nowrap",
                  minHeight: "auto",
                  textTransform: "uppercase",
                  alignSelf: "center",
                }}
              >
                {en.common.publishing}
              </Button>
              <Menu
                id="publishing-menu"
                anchorEl={publishingAnchorEl}
                keepMounted
                open={Boolean(publishingAnchorEl)}
                onClose={handlePublishingClose}
                sx={{ ...rightLinkNarrow }}
              >
                <MenuItem
                  onClick={() => {
                    handlePublishingClose();
                    window.open("/publishing");
                  }}
                >
                  publishing info
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handlePublishingClose();
                    window.open("/submissions");
                  }}
                >
                  submissions
                </MenuItem>
              </Menu>

              {isAuthenticated && (
                <Link
                  variant="h6"
                  component="button"
                  onClick={() =>
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    })
                  }
                  underline="none"
                  sx={{
                    ...rightLink,
                    color: "secondary.light",
                    whiteSpace: "nowrap",
                  }}
                >
                  {en.common.logout}
                </Link>
              )}

              {!isAuthenticated && (
                <Link
                  variant="h6"
                  component="button"
                  onClick={() => loginWithRedirect()}
                  underline="none"
                  sx={{
                    ...rightLink,
                    color: "secondary.light",
                    whiteSpace: "nowrap",
                  }}
                >
                  {en.common.login}
                </Link>
              )}
            </Box>
          )}

          {/* Mobile Hamburger Menu */}
          {isMobile && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
                sx={{ color: "secondary.light" }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="mobile-menu"
                anchorEl={mobileMenuAnchorEl}
                keepMounted
                open={Boolean(mobileMenuAnchorEl)}
                onClose={handleMobileMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/about");
                  }}
                >
                  {en.marketing.about}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/features");
                  }}
                >
                  {en.marketing.features}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/guidelines");
                  }}
                >
                  {en.marketing.communityGuideline}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/dashboard");
                  }}
                >
                  {en.common.dashboard}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/community");
                  }}
                >
                  {en.common.community}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/publishing");
                  }}
                >
                  {en.common.publishing}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/submissions");
                  }}
                >
                  submissions
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMobileMenuClose();
                    window.open("/publications");
                  }}
                >
                  {en.common.publications}
                </MenuItem>
                {isAuthenticated && (
                  <MenuItem
                    onClick={() => {
                      handleMobileMenuClose();
                      logout({
                        logoutParams: { returnTo: window.location.origin },
                      });
                    }}
                  >
                    {en.common.logout}
                  </MenuItem>
                )}
                {!isAuthenticated && (
                  <MenuItem
                    onClick={() => {
                      handleMobileMenuClose();
                      loginWithRedirect();
                    }}
                  >
                    {en.common.login}
                  </MenuItem>
                )}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
    </React.Fragment>
  );
}

export default AppAppBar;
